import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import type { HeroPhoto } from '../lib/api';

/**
 * Parallax photo stack. Shows three "lanes" that scroll at different
 * speeds, creating depth. Uses only curated photos (publish_to_showcase=1
 * on the Builder side). When there are < 3 photos, we gracefully
 * consolidate to a single centered lane. When there are zero, we
 * render a contemplative empty state instead of a blank section.
 *
 * Photos deliberately retain their native aspect ratio inside a fixed
 * frame so tall/wide mixes feel like a curated contact sheet rather
 * than a grid.
 */
export function Gallery({ photos }: { photos: HeroPhoto[] }) {
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  // Three lanes, three parallax rates. Middle lane is slowest.
  const yLeft = useTransform(scrollYProgress, [0, 1], ['-10%', '10%']);
  const yMid  = useTransform(scrollYProgress, [0, 1], ['-4%', '4%']);
  const yRight = useTransform(scrollYProgress, [0, 1], ['-12%', '12%']);

  if (photos.length === 0) {
    return (
      <section
        ref={ref}
        className="relative border-t border-line bg-bg py-32"
        aria-label="Galeria"
      >
        <div className="mx-auto max-w-3xl px-6 text-center">
          <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-accent">
            Galeria
          </p>
          {/* Wave 299 — second clause was text-ink-subtle (48% alpha) on
              a 24px display line; the audit flagged it as "ghost
              paragraph" because the second half visibly fades below the
              first. Bumped to ink-muted so both clauses share the same
              tonal register (still quieter than ink). */}
          <p className="mt-10 font-display text-2xl font-light leading-snug tracking-tight text-ink-muted">
            A obra é documentada
            <span className="block text-ink-muted"> conforme cada etapa avança.</span>
          </p>
        </div>
      </section>
    );
  }

  // Distribute photos across lanes. Round-robin keeps the lanes
  // balanced even with small counts.
  const lanes: HeroPhoto[][] = [[], [], []];
  photos.forEach((p, i) => {
    const lane = lanes[i % 3];
    if (lane) lane.push(p);
  });

  return (
    <section
      ref={ref}
      className="relative overflow-hidden border-t border-line bg-bg py-32 sm:py-40"
      aria-label="Galeria"
    >
      <div className="mx-auto mb-20 max-w-6xl px-6 sm:px-12 lg:px-20">
        <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-accent">
          Galeria
        </p>
        <h2 className="mt-5 max-w-[18ch] font-display text-4xl font-light leading-[1.05] tracking-tighter text-ink sm:text-5xl">
          Registros da obra.
        </h2>
      </div>

      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 px-6 sm:grid-cols-3 sm:gap-8 sm:px-12 lg:px-20">
        <motion.div style={{ y: yLeft }} className="space-y-6 pt-8 sm:pt-16">
          {lanes[0]!.map((p) => (
            <PhotoCard key={p.id} photo={p} />
          ))}
        </motion.div>
        <motion.div style={{ y: yMid }} className="space-y-6 hidden sm:block">
          {lanes[1]!.map((p) => (
            <PhotoCard key={p.id} photo={p} />
          ))}
        </motion.div>
        <motion.div style={{ y: yRight }} className="space-y-6 pt-4 hidden sm:block sm:pt-24">
          {lanes[2]!.map((p) => (
            <PhotoCard key={p.id} photo={p} />
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function PhotoCard({ photo }: { photo: HeroPhoto }) {
  const aspectRatio =
    photo.width && photo.height ? `${photo.width} / ${photo.height}` : '4 / 3';
  return (
    <figure className="group relative overflow-hidden rounded-[2px] bg-bg-card">
      <div
        className="relative w-full overflow-hidden"
        style={{ aspectRatio }}
      >
        <img
          src={photo.url}
          alt={photo.caption || 'Registro da obra — Gênese'}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'linear-gradient(to bottom, transparent 60%, rgba(5,5,5,0.85) 100%)',
          }}
        />
      </div>
      {photo.caption && (
        <figcaption className="pointer-events-none absolute inset-x-0 bottom-0 p-5 text-[12px] leading-snug text-ink/85 sm:text-[13px]">
          {photo.caption}
        </figcaption>
      )}
    </figure>
  );
}
